import React, { useEffect, useRef } from 'react';
import { ThemeProvider, ThemeOptions, createMuiTheme, CssBaseline } from '@material-ui/core';
import { SaurusTheme } from 'theme';
import { ValidarCor } from 'utils';
import { TemaModel } from 'api/cardapio/models/tema/tema-model';
import { EmpresaModel } from 'api/cardapio/models/empresa/empresa-model';
import { useCardapioApi, useMasterApi } from 'modulos/cardapio/containers/cardapio/hooks';
import { useCardapioDadosStore } from 'services/store/cardapio-dados-store';
import { getIDFromUrl } from 'utils/get-id-url';
const tinycolor = require('tinycolor2')

interface EmpresaTemaContextType {
  tema?: TemaModel;
  isSuccess?: boolean;
  empresaLocalizada?: boolean;
  carregandoEmpresa?: boolean;
  atualizarTema?: (tema?: TemaModel) => any;
  atualizarEmpresa?: (id?: any) => any;
  empresa?: EmpresaModel;
}

const EmpresaTemaContext = React.createContext<EmpresaTemaContextType>({
  tema: undefined,
  carregandoEmpresa: undefined,
  atualizarTema: undefined,
  empresa: undefined,
});

interface EmpresaDadosContextType {
  empresaDados?: EmpresaModel;
  isSuccess?: boolean;
  carregandoEmpresa?: boolean;
  atualizarEmpresaDados?: (id?: any) => any;
}

const EmpresaDadosContext = React.createContext<EmpresaDadosContextType>({
  empresaDados: undefined,
  isSuccess: undefined,
  carregandoEmpresa: undefined,
  atualizarEmpresaDados: undefined,
});

export const useEmpresaTema = () => {
  return React.useContext(EmpresaTemaContext);
};

export const useEmpresaDados = () => {
  return React.useContext(EmpresaDadosContext)
}
export interface EmpresaTemaProviderProps {
  children: React.ReactNode;
}

const obterTipo = (type?: string | number) => {
  if (typeof type === 'number') {
    return type === 0 ? 'light' : 'dark';
  }

  return type === 'dark' || type === '1' ? 'dark' : 'light';
}

const obterContrastColor = (color) => {
  return tinycolor(color).getBrightness() > 200 ? tinycolor(color).darken(50).toString() : '#fff'
}

export const EmpresaTema = ({ children }: EmpresaTemaProviderProps) => {
  const [tema, setarTema] = React.useState(SaurusTheme);
  const empresaId = getIDFromUrl('empresaId');
  const atualizouTema = useRef(false);
  const { dados: apiMasterDados, operacoes } = useMasterApi(empresaId);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dados } = useCardapioApi(empresaId)
  const configuracoes = useCardapioDadosStore(state => state.cardapioDados?.configuracoes)
  const { atualizarCadastroEmpresa } = operacoes;

  useEffect(() => {
    if (empresaId) {
    }
  }, [atualizarCadastroEmpresa, empresaId])

  const {
    primaryContrast,
    primaryDark,
    primaryLight,
    primaryMain,
    secondaryContrast,
    secondaryDark,
    secondaryLight,
    secondaryMain,
    type,
  } = configuracoes || {};



  const saurusTema = React.useMemo(() => {
    return {
      primaryMain: ValidarCor(primaryMain)
        ? (primaryMain as string)
        : SaurusTheme.palette.primary.main,
      primaryDark: ValidarCor(primaryDark)
        ? (primaryDark as string)
        : SaurusTheme.palette.primary.dark,
      primaryLight: ValidarCor(primaryLight)
        ? (primaryLight as string)
        : SaurusTheme.palette.primary.light,
      primaryContrast: ValidarCor(primaryContrast)
        ? (primaryContrast as string)
        : SaurusTheme.palette.primary.contrastText,
      secondaryMain: ValidarCor(secondaryMain)
        ? (secondaryMain as string)
        : SaurusTheme.palette.secondary.main,
      secondaryDark: ValidarCor(secondaryDark)
        ? (secondaryDark as string)
        : SaurusTheme.palette.secondary.dark,
      secondaryLight: ValidarCor(secondaryLight)
        ? (secondaryLight as string)
        : SaurusTheme.palette.secondary.light,
      secondaryContrast: ValidarCor(secondaryContrast)
        ? (secondaryContrast as string)
        : SaurusTheme.palette.secondary.contrastText,
      textPrimary: SaurusTheme.palette.text.primary,
      textSecondary: SaurusTheme.palette.text.secondary,
      type: type || 0,
    } as TemaModel;
  }, [primaryContrast, primaryDark, primaryLight, primaryMain, secondaryContrast, secondaryDark, secondaryLight, secondaryMain, type]);


  const atualizarTema = React.useCallback(
    (input?: TemaModel) => {
      const tipo = obterTipo(input?.type);
      const proximoTema = createMuiTheme({
        palette: {
          primary: {
            main: ValidarCor(input?.primaryMain)
              ? input?.primaryMain
              : SaurusTheme.palette.primary.main,
            light: ValidarCor(input?.primaryLight)
              ? input?.primaryLight
              : SaurusTheme.palette.primary.light,
            dark: ValidarCor(input?.primaryDark)
              ? input?.primaryDark
              : SaurusTheme.palette.primary.dark,
            contrastText: ValidarCor(input?.primaryMain)
              ? obterContrastColor(input?.primaryMain)
              : obterContrastColor(input?.primaryMain),
          },
          secondary: {
            main: ValidarCor(input?.secondaryMain)
              ? input?.secondaryMain
              : SaurusTheme.palette.secondary.main,
            light: ValidarCor(input?.secondaryLight)
              ? input?.secondaryLight
              : SaurusTheme.palette.secondary.light,
            dark: ValidarCor(input?.secondaryDark)
              ? input?.secondaryDark
              : SaurusTheme.palette.secondary.dark,
            contrastText: ValidarCor(input?.secondaryMain)
              ? obterContrastColor(input?.secondaryMain)
              : obterContrastColor(input?.secondaryMain),
          },
          type: tipo,
          background: {
            default: tipo === 'light' ? '#fafafa' : '#121212',
          },
        },
        overrides: SaurusTheme.overrides,
        props: SaurusTheme.props,
        shape: SaurusTheme.shape
      } as ThemeOptions);
      setarTema(proximoTema);
    },
    [],
  );

  useEffect(() => {
    if (empresaId) {
      atualizarTema(saurusTema);
      atualizouTema.current = true;
    }
  }, [atualizarTema, empresaId, saurusTema]);

  return (
    <ThemeProvider theme={tema}>
      <CssBaseline>
        <EmpresaTemaContext.Provider
          value={{
            carregandoEmpresa: apiMasterDados.isLoading || apiMasterDados.isFetching,
            atualizarTema,
            tema: apiMasterDados.data?.configuracoes,
            empresa: apiMasterDados.data?.empresa
          }}
        >
          {children}
        </EmpresaTemaContext.Provider>
      </CssBaseline>
    </ThemeProvider>
  );
};
